import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import RoundedPaper from 'shared/components/RoundedPaper';
import GoogleWatchdogService from 'services/GoogleWatchdogService';
import useKeymap from 'shared/hooks/useKeymap';
import { changeLogCache } from 'pages/GoogleWatchdog/cache';
import { localDay, parseTs } from 'pages/GoogleWatchdog/logic';
import { ChangeGroup, ChangeRow, STORES } from 'pages/GoogleWatchdog/types';
import LogChangeDialog from './LogChangeDialog';

const service = new GoogleWatchdogService();
const PAGE_SIZE = 100;
const SOURCES = ['', 'google_ads', 'shopify_theme', 'wordpress', 'manual', 'resolution'];

// Background tints with dark text (AA on white); severity colors stay off these badges.
const SOURCE_BADGES: Record<string, { bg: string; fg: string; label: string }> = {
  google_ads: { bg: '#DBEAFE', fg: '#1E40AF', label: 'google ads' },
  shopify_theme: { bg: '#D1FAE5', fg: '#065F46', label: 'shopify theme' },
  wordpress: { bg: '#E2E8F0', fg: '#334155', label: 'wordpress' },
  manual: { bg: '#F1F5F9', fg: '#475569', label: 'manual' },
  resolution: { bg: '#E0E7FF', fg: '#3730A3', label: 'resolution' },
};

const timeOfDay = (ts: string): string =>
  parseTs(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function dayLabel(day: string, now: Date): string {
  const todayMs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const [y, m, d] = day.split('-').map(Number);
  const diff = Math.round((todayMs - new Date(y, m - 1, d).getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const isSystemActor = (actor: string | null): boolean => !actor || /watchdog|system/i.test(actor);
/** "inno@sbly.com" → "inno"; system rows read "watchdog". */
const shortActor = (actor: string | null): string => (isSystemActor(actor) ? 'watchdog' : actor!.split('@')[0]);

function SourceBadge({ source }: { source: string }) {
  const badge = SOURCE_BADGES[source] ?? { bg: '#F1F5F9', fg: '#475569', label: source };
  return (
    <Box component="span" sx={{
      bgcolor: badge.bg, color: badge.fg, px: 0.75, py: 0.1, borderRadius: 1,
      fontSize: 10.5, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {badge.label}
    </Box>
  );
}

function ActorCell({ actor }: { actor: string | null }) {
  const name = shortActor(actor);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: 96, flexShrink: 0, minWidth: 0 }}>
      {isSystemActor(actor) ? (
        <SettingsOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
      ) : (
        <Box sx={{
          width: 16, height: 16, borderRadius: '50%', bgcolor: '#E0E7FF', color: '#3730A3',
          fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {name[0].toUpperCase()}
        </Box>
      )}
      <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>{name}</Typography>
    </Box>
  );
}

/** old struck-through slate → new in semantic green; renders only when a value exists. */
function Delta({ oldValue, newValue }: { oldValue: string | null; newValue: string | null }) {
  if (!oldValue && !newValue) return null;
  return (
    <Typography variant="caption" noWrap sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
      {oldValue && <Box component="span" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>{oldValue}</Box>}
      {oldValue && newValue && <Box component="span" sx={{ color: 'text.disabled' }}> → </Box>}
      {newValue && <Box component="span" sx={{ color: '#059669', fontWeight: 600 }}>{newValue}</Box>}
    </Typography>
  );
}

function MemberRow({ row }: { row: ChangeRow }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, pl: 7, pr: 2, py: 0.4, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', width: 52, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
        {timeOfDay(row.changed_at)}
      </Typography>
      <Typography variant="caption" sx={{ minWidth: 0, flex: 1, color: 'text.secondary' }}>{row.summary}</Typography>
      <Delta oldValue={row.old_value} newValue={row.new_value} />
    </Box>
  );
}

type Expansion = ChangeRow[] | 'loading' | 'error';

function GroupRow({ row, cursor, expansion, onToggle }: {
  row: ChangeGroup;
  cursor: boolean;
  expansion: Expansion | undefined;
  onToggle: (row: ChangeGroup) => void;
}) {
  const burst = row.event_count > 1;
  return (
    <Box data-change-key={row.change_key}>
      <Box
        onClick={() => burst && onToggle(row)}
        tabIndex={burst ? 0 : undefined}
        onKeyDown={e => { if (burst && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onToggle(row); } }}
        sx={{
          display: 'flex', alignItems: 'baseline', gap: 1, px: 2, py: 0.75, minWidth: 0,
          cursor: burst ? 'pointer' : 'default',
          bgcolor: cursor ? 'rgba(99,102,241,0.08)' : 'transparent',
          outline: cursor ? '1px solid #6366F1' : 'none', outlineOffset: -1,
          '&:hover': burst ? { bgcolor: cursor ? 'rgba(99,102,241,0.08)' : 'rgba(100,116,139,0.06)' } : undefined,
          '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: -2 },
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', width: 52, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {timeOfDay(row.changed_at)}
        </Typography>
        <ActorCell actor={row.actor} />
        {row.store && <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', flexShrink: 0 }}>{row.store}</Typography>}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {row.summary}
            {row.url && (
              <a href={row.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                <LaunchIcon sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'middle', color: 'text.disabled' }} />
              </a>
            )}
          </Typography>
          {row.rationale && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{row.rationale}</Typography>
          )}
        </Box>
        <Delta oldValue={row.old_value} newValue={row.new_value} />
        {burst && (
          <Chip
            label={`${row.event_count} changes`} size="small"
            onClick={e => { e.stopPropagation(); onToggle(row); }}
            sx={{ height: 18, fontSize: 11, flexShrink: 0 }}
          />
        )}
        <SourceBadge source={row.source} />
        {row.linked_item_id && (
          <Link to={`/google-watchdog/items/${encodeURIComponent(row.linked_item_id)}`} onClick={e => e.stopPropagation()}
            style={{ fontSize: 12, flexShrink: 0 }}>
            item
          </Link>
        )}
      </Box>
      {expansion === 'loading' && (
        <Box sx={{ pl: 7, py: 0.5 }}><CircularProgress size={14} /></Box>
      )}
      {expansion === 'error' && (
        <Typography variant="caption" sx={{ pl: 7, py: 0.5, display: 'block', color: 'error.main' }}>
          Failed to load the changes in this group.
        </Typography>
      )}
      {Array.isArray(expansion) && (
        <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', ml: 4, mb: 0.5 }}>
          {expansion.map(m => <MemberRow key={m.change_key} row={m} />)}
        </Box>
      )}
    </Box>
  );
}

export default function ChangeLog() {
  const history = useHistory();
  // The detail view's cross-link lands here with ?campaign=<id or name>.
  const campaign = new URLSearchParams(useLocation().search).get('campaign') ?? '';
  // Filters always start empty on mount, so the cache seeds the default view
  // instantly (stale-while-revalidate); filtered/paged loads stay live-only.
  const [rows, setRows] = useState<ChangeGroup[]>(() => (campaign ? [] : changeLogCache.rows ?? []));
  const [loading, setLoading] = useState(rows.length === 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState('');
  const [store, setStore] = useState('');
  const [source, setSource] = useState('');
  const [actor, setActor] = useState('');
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [expanded, setExpanded] = useState<Record<string, Expansion>>({});
  const [cursor, setCursor] = useState(-1);
  const [logOpen, setLogOpen] = useState(false);
  const cacheSeed = useRef(!campaign && changeLogCache.rows !== null);
  // Bumped on every full reload so an in-flight "Load more" from the previous
  // filter set can't append its stale page onto the fresh rows.
  const loadGen = useRef(0);

  const filters = useMemo(
    () => ({
      store: store || undefined, source: source || undefined, actor: actor || undefined,
      campaign: campaign || undefined, q: q || undefined,
    }),
    [store, source, actor, campaign, q],
  );

  const load = useCallback(async (background = false) => {
    loadGen.current += 1;
    if (!background) setLoading(true);
    setError('');
    const isDefaultView = !store && !source && !actor && !campaign && !q;
    try {
      const fresh = await service.listChangeGroups({ ...filters, limit: PAGE_SIZE, offset: 0 });
      setRows(fresh);
      setExhausted(fresh.length < PAGE_SIZE);
      setExpanded({});
      setCursor(-1);
      if (isDefaultView) changeLogCache.rows = fresh;
    } catch (e: any) {
      setError(e.message);
    } finally {
      if (!background) setLoading(false);
    }
  }, [filters, store, source, actor, campaign, q]);
  useEffect(() => {
    load(cacheSeed.current);
    cacheSeed.current = false;
  }, [load]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    const gen = loadGen.current;
    try {
      const more = await service.listChangeGroups({ ...filters, limit: PAGE_SIZE, offset: rows.length });
      if (gen !== loadGen.current) return; // filters changed mid-flight — page belongs to the old set
      setRows(prev => {
        // A write landing between two reads can shift the offset boundary and repeat a
        // row across pages — drop repeats so React keys stay unique.
        const seen = new Set(prev.map(r => r.change_key));
        return [...prev, ...more.filter(r => !seen.has(r.change_key))];
      });
      setExhausted(more.length < PAGE_SIZE);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  }, [filters, rows.length]);

  // Debounce search: q updates 400ms after the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => setQ(qDraft), 400);
    return () => clearTimeout(t);
  }, [qDraft]);

  const toggle = useCallback((row: ChangeGroup) => {
    const key = row.change_key;
    const current = expanded[key];
    if (current && current !== 'error') {
      setExpanded(prev => { const { [key]: _drop, ...rest } = prev; return rest; });
      return;
    }
    setExpanded(prev => ({ ...prev, [key]: 'loading' }));
    service.listBurstMembers(key)
      .then(members => setExpanded(p => (p[key] === 'loading' ? { ...p, [key]: members } : p)))
      .catch(() => setExpanded(p => (p[key] === 'loading' ? { ...p, [key]: 'error' } : p)));
  }, [expanded]);

  // j/k cursor + Enter/→ burst expansion, matching the queue's keyboard idiom.
  const moveCursor = useCallback((delta: number) => {
    setCursor(prev => Math.min(rows.length - 1, Math.max(0, prev + delta)));
  }, [rows.length]);
  useEffect(() => {
    const key = rows[cursor]?.change_key;
    if (!key) return;
    document.querySelector(`[data-change-key="${CSS.escape(key)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor, rows]);
  useKeymap({
    j: () => moveCursor(1),
    k: () => moveCursor(-1),
    Enter: () => { const row = rows[cursor]; if (row && row.event_count > 1) toggle(row); },
    ArrowRight: () => { const row = rows[cursor]; if (row && row.event_count > 1) toggle(row); },
  }, !logOpen);

  // Actor filter options come from the loaded rows (the API has no actor index);
  // the current selection stays listed even when its rows scroll out of the page.
  const actorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.actor && !isSystemActor(r.actor)) set.add(r.actor);
    if (actor) set.add(actor);
    return Array.from(set).sort();
  }, [rows, actor]);

  const now = new Date();
  const days = useMemo(() => {
    const groups: { day: string; rows: ChangeGroup[] }[] = [];
    for (const row of rows) {
      const day = localDay(row.changed_at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.rows.push(row);
      else groups.push({ day, rows: [row] });
    }
    return groups;
  }, [rows]);

  return (
    <Box sx={{ pb: 6 }}>
      {/* The main layout provides the page gutter (p: 4) — keep headers flush
          with every other page's. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>Change Log</Typography>
        <TextField size="small" placeholder="Search summary, actor, rationale…" value={qDraft}
          onChange={e => setQDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') (e.target as HTMLInputElement).blur(); }}
          sx={{ width: 260 }} />
        <TextField size="small" select label="Source" value={source} onChange={e => setSource(e.target.value)} sx={{ width: 150 }}>
          {SOURCES.map(s => <MenuItem key={s} value={s}>{s ? (SOURCE_BADGES[s]?.label ?? s) : 'All'}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Who" value={actor} onChange={e => setActor(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          {actorOptions.map(a => <MenuItem key={a} value={a}>{shortActor(a)}</MenuItem>)}
        </TextField>
        <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setLogOpen(true)}>Log a change</Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, flexWrap: 'wrap' }}>
        <Chip label="All" size="small" variant={store ? 'outlined' : 'filled'} onClick={() => setStore('')} />
        {STORES.map(s => (
          <Chip key={s} label={s} size="small" variant={store === s ? 'filled' : 'outlined'}
            onClick={() => setStore(store === s ? '' : s)} />
        ))}
        {campaign && (
          <Chip label={`campaign: ${campaign}`} size="small" color="primary"
            onDelete={() => history.replace('/change-log')} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {rows.length}{exhausted ? '' : '+'} change{rows.length === 1 ? '' : 's'}
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }} action={<Button onClick={() => load()}>Retry</Button>}>{error}</Alert>}
      <RoundedPaper sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>
            No changes match these filters.
          </Typography>
        ) : (
          <>
            {days.map(group => (
              <Box key={group.day}>
                <Typography variant="caption" sx={{
                  position: 'sticky', top: 0, zIndex: 1, display: 'block', px: 2, py: 0.5,
                  bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider',
                  fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3,
                }}>
                  {dayLabel(group.day, now)}
                </Typography>
                {group.rows.map(row => (
                  <GroupRow key={row.change_key} row={row} cursor={rows[cursor]?.change_key === row.change_key}
                    expansion={expanded[row.change_key]} onToggle={toggle} />
                ))}
              </Box>
            ))}
            {!exhausted && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <Button size="small" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </Box>
            )}
          </>
        )}
      </RoundedPaper>
      <LogChangeDialog open={logOpen} onClose={() => setLogOpen(false)}
        onLog={async body => { await service.logChange(body); await load(); }} />
    </Box>
  );
}
